import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabaseService } from '../../utils/supabaseService';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';

const PublicArtists = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [genreFilter, setGenreFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);

  const load = useCallback(async ({ reset = false } = {}) => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const nextPage = reset ? 1 : page;
      const data = await supabaseService.listPublicArtistsPage({ page: nextPage, pageSize: 30, search });
      if (reset) {
        setArtists(data);
      } else {
        setArtists(prev => [...prev, ...data]);
      }
      setHasMore(data.length === 30); // heuristique
      if (reset) setPage(1);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [search, page, loading]);

  // Recharge quand le terme de recherche change
  useEffect(() => {
    setPage(1);
    load({ reset: true });
  }, [search, load]);

  // Intersecion Observer pour infinite scroll
  useEffect(() => {
    if (!hasMore || loading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPage(p => p + 1);
      }
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  // Charger page suivante quand page state augmente (sauf premier reset déjà géré)
  useEffect(() => {
    if (page > 1) {
      load();
    }
  }, [page, load]);

  return (
    <div className="pt-24 pb-16 max-w-6xl mx-auto px-4">
      <h1 className="text-3xl font-bold mb-6 flex items-center"><Icon name="Users" className="mr-3" /> Artistes</h1>
  <p className="text-muted-foreground mb-8 text-sm">Annuaire public. Recherche full‑text (nom, ville) & match simple sur genres. Seuls les profils publics apparaissent.</p>
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <Input placeholder="Rechercher un artiste" value={search} onChange={e=>setSearch(e.target.value)} />
  <Button onClick={() => load({ reset: true })} disabled={loading}>{loading ? '...' : 'Rechercher'}</Button>
        <Input placeholder="Genre" value={genreFilter} onChange={e=>setGenreFilter(e.target.value)} />
        <Input placeholder="Ville" value={cityFilter} onChange={e=>setCityFilter(e.target.value)} />
      </div>
      {error && <div className="text-sm text-red-500 mb-4">{error}</div>}
  <div className="grid gap-4 md:grid-cols-3">
        {artists
          .filter(a => !genreFilter || (a.genres || []).some(g => g.toLowerCase().includes(genreFilter.toLowerCase())))
          .filter(a => !cityFilter || (a.city || '').toLowerCase().includes(cityFilter.toLowerCase()))
          .map(a => (
          <div key={a.user_id} className="border border-border rounded-lg p-4 bg-card flex flex-col">
            <h2 className="font-semibold text-lg mb-1">{a.display_name || 'Sans nom'}</h2>
            <div className="text-xs text-muted-foreground mb-2">{a.city || 'Ville'}{a.country ? ', ' + a.country : ''}</div>
            {a.genres?.length > 0 && <div className="text-xs mb-2">Genres: {a.genres.join(', ')}</div>}
            {a.cachet_min && <div className="text-xs">Cachet: {a.cachet_min}{a.cachet_max ? ' - ' + a.cachet_max : ''} €</div>}
            <div className="mt-auto pt-3">
              <Link to={`/artists/${a.user_id}`}>
                <Button variant="outline" size="sm">Voir profil</Button>
              </Link>
            </div>
          </div>
        ))}
        {!artists.length && !loading && <div className="text-sm text-muted-foreground">Aucun artiste trouvé.</div>}
        <div ref={sentinelRef} className="h-10 col-span-full flex items-center justify-center text-xs text-muted-foreground">
          {loading ? 'Chargement...' : (hasMore ? '' : 'Fin des résultats')}
        </div>
      </div>
    </div>
  );
};

export default PublicArtists;